<template>
  <VCol v-if="!userData.user.isVerified" class="pa-0" cols="12">
    <VAlert density="compact" :style="backgroundStyle">
      <div class="d-flex align-center">
        <div class="d-flex flex-column mb-1">
          <div class="d-flex align-center">
            <VIcon class="mr-2" icon="$warning" color="orange-darken-3" size="28" />
            <span class="text-h6 text-orange-darken-3 text--emphasis font-weight-medium">
              Account Verification
            </span>
          </div>

          <span class="ml-10 text-body-1 font-weight-medium">
            Please check your emails for a link to verify your account.
          </span>
        </div>

        <template v-if="smAndUp">
          <VSpacer />
          <VBtn :loading="verifyEmailLoading" :disabled="isButtonDisabled" @click="resendVerificationEmail"
            :variant="theme.global.name.value === 'dark' ? 'tonal' : 'flat'" color="orange-darken-3">
            <VIcon class="mr-2" icon="$mdiEmailSync" size="large" />
            {{ buttonLabel }}
          </VBtn>
        </template>
      </div>

      <div v-if="!smAndUp" class="d-flex">
        <VSpacer />
        <VBtn :loading="verifyEmailLoading" :disabled="isButtonDisabled" @click="resendVerificationEmail"
          :variant="theme.global.name.value === 'dark' ? 'tonal' : 'flat'" color="orange-darken-3">
          <VIcon class="mr-2" icon="$mdiEmailSync" size="large" />
          {{ buttonLabel }}
        </VBtn>
      </div>
    </VAlert>
  </VCol>
</template>

<script setup lang="ts">
import { useTheme, useDisplay } from 'vuetify';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useUserDataStore } from '@S/userDataStore.ts';
import { useBackgroundStyle } from '@R/constants/componentStyle.ts';

let userData = useUserDataStore();
const { authToken } = useACookie();
dayjs.extend(relativeTime);
const notifier = useNotifier();
const backgroundStyle = useBackgroundStyle();
const theme = useTheme();
const { smAndUp } = useDisplay();

const verifyEmailLoading = ref(false);
const isButtonDisabled = ref(false);
const buttonLabel = ref('Resend Email');
const countdownInterval = ref<ReturnType<typeof setInterval> | null>(null);

const updateButtonState = () => {
  const nextAllowedTime = dayjs(userData.user.nextResendAllowedTime);
  const now = dayjs();

  if (now.isBefore(nextAllowedTime)) {
    isButtonDisabled.value = true;

    const updateCountdown = () => {
      const remaining = nextAllowedTime.diff(dayjs(), 'second');
      if (remaining <= 0) {
        isButtonDisabled.value = false;
        buttonLabel.value = 'Resend Email';
        if (countdownInterval.value) clearInterval(countdownInterval.value);
      } else {
        const minutes = Math.floor(remaining / 60);
        const seconds = remaining % 60;
        buttonLabel.value = `${minutes}m ${seconds}s`;
      }
    };

    updateCountdown();
    countdownInterval.value = setInterval(updateCountdown, 1000);
  } else {
    isButtonDisabled.value = false;
    buttonLabel.value = 'Resend Email';
  }
};

watchEffect(updateButtonState);

interface ApiResponse {
  success: boolean;
  message: string;
  nextResendAllowedTime?: string;
}

const resendVerificationEmail = async () => {
  verifyEmailLoading.value = true;

  try {
    const { data: response, error } = await useFetch<ApiResponse>('/api/v1/email/verify', {
      method: "POST",
      headers: { Authorization: `Bearer ${authToken.value}` },
      body: {
        action: 'resend'
      }
    });

    if (error.value) {
      throw new Error(error.value.message);
    }

    if (response.value?.success) {
      notifier.toast({
        icon: '$mdiCheck',
        type: 'success',
        title: 'Success',
        text: response.value.message,
        color: 'green',
        onClick: () => { },
        onClose: () => { }
      })

      userData.user.nextResendAllowedTime = dayjs().add(15, 'minutes').toISOString();
      updateButtonState();
    } else {
      if (response.value?.nextResendAllowedTime) {
        userData.user.nextResendAllowedTime = response.value.nextResendAllowedTime;
      }
      throw new Error(response.value?.message || "An unexpected error occurred.");
    }
  } catch (error: unknown) {
    console.error("Error resending verification email:", (error as Error).message);
    notifier.toast({
      icon: '$mdiAlert',
      type: 'error',
      title: 'Error',
      text: (error as Error).message,
      color: 'red',
      onClick: () => { },
      onClose: () => { }
    })
  } finally {
    verifyEmailLoading.value = false;
  }
};
</script>